import React from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  Paper,
  Stack,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Security,
  Shield,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const LoginContainer = styled(Box)`
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 32px 16px;
  overflow-x: hidden;
  overflow-y: auto;

  & > * {
    position: relative;
    z-index: 1;
  }

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: rotate 30s linear infinite;
    pointer-events: none;
    z-index: 0;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
  position: relative;
  overflow: visible;

  @media (max-width: 600px) {
    border-radius: 18px;
  }
`;

const LogoBox = styled(Box)`
  width: 120px;
  height: 120px;
  background: transparent;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -50px auto 20px;
  box-shadow: none;

  @media (max-width: 600px) {
    width: 96px;
    height: 96px;
    border-radius: 20px;
    margin: -42px auto 16px;
  }

  @media (max-height: 720px) {
    margin: -40px auto 16px;
  }
`;

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [show, setShow] = React.useState(false);
  const [remember, setRemember] = React.useState(false);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [adminKey, setAdminKey] = React.useState(() => {
    if (typeof window === "undefined") return "";
    const rememberMe = window.localStorage.getItem("rememberMe") === "true";
    if (!rememberMe) return "";
    return window.localStorage.getItem("adminKey") || "";
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const rememberMe = window.localStorage.getItem("rememberMe") === "true";
    setRemember(rememberMe);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!adminKey.trim()) {
        setError("Admin anahtarı gerekli");
        setLoading(false);
        return;
      }

      const res = await fetch("/api/content/ping", {
        headers: { "x-admin-key": adminKey },
      }).catch(() => null);

      if (!res) {
        throw new Error("Sunucuya ulaşılamadı");
      } else if (res.status === 401) {
        throw new Error("Admin anahtarı geçersiz");
      } else if (!res.ok) {
        throw new Error("Doğrulama başarısız");
      }

      const userData = {
        id: 1,
        username: "Admin",
        email: "admin@yanmazkapi.com",
        role: "admin",
      };

      localStorage.setItem("authToken", "admin");
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("adminKey", adminKey);
      if (remember) localStorage.setItem("rememberMe", "true");
      else localStorage.removeItem("rememberMe");

      navigate("/admin");
    } catch (err: any) {
      setError(err?.message || "Giriş yapılamadı");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Container maxWidth="sm">
        <LoginCard>
          <LogoBox>
            <Box
              component="img"
              src="/brand/yanmazkapi-logo.jpg"
              alt="Yanmaz Kapı"
              sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
          </LogoBox>

          <CardContent sx={{ p: 4, pt: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                textAlign: "center",
                mb: 1,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Yönetim Paneli
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mb: 3 }}
            >
              Yanmaz Kapı Admin Girişi
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Hesap"
                value="admin@yanmazkapi.com"
                sx={{ mb: 2 }}
                disabled
              />

              <TextField
                fullWidth
                label="Admin Anahtarı"
                type={show ? "text" : "password"}
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Admin anahtarını girin"
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShow((s) => !s)}
                        edge="end"
                        disabled={loading}
                      >
                        {show ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Beni hatırla"
                />
              </Stack>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.5,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a67d8 0%, #6b46a2 100%)",
                  },
                }}
              >
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">
                GÜVENLİK BİLGİSİ
              </Typography>
            </Divider>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "rgba(102, 126, 234, 0.05)",
                borderColor: "rgba(102, 126, 234, 0.2)",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={1}>
                  <Security sx={{ fontSize: 18, color: "#667eea" }} />
                  <Shield sx={{ fontSize: 18, color: "#764ba2" }} />
                </Stack>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    256-bit SSL Şifreleme
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Bağlantınız güvenli ve şifrelidir
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </CardContent>
        </LoginCard>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            onClick={() => navigate("/")}
            sx={{
              color: "white",
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            ← Ana Sayfaya Dön
          </Button>
        </Box>
      </Container>
    </LoginContainer>
  );
};

export default AdminLogin;
